import { computed, toValue, type MaybeRef, type Ref } from 'vue'

import { useRealtimeSubscription } from '@ja/realtime'
import type { RealtimeFrame } from '@ja/realtime'

export interface NewCommunicationEvent {
  action: 'new_communication'
  communicationId: string
  type: string
  title: string
  senderName: string | null
  priority: 'low' | 'normal' | 'high' | 'urgent'
  requiresAcknowledgment: boolean
  timestamp: string
}

export interface UnreadCountChangedEvent {
  action: 'unread_count_changed'
  totalUnread: number
  byType: Record<string, number>
  timestamp: string
}

export interface RecipientStateChangedEvent {
  action: 'read' | 'acknowledged'
  communicationId: string
  recipientId: string | null
  acknowledgmentLevel?: 'read' | 'accepted' | 'signed'
  timestamp: string
}

type InboxPayload = NewCommunicationEvent | UnreadCountChangedEvent | RecipientStateChangedEvent

export interface UseInboxChannelOptions {
  onNewCommunication?: (event: NewCommunicationEvent) => void
  onUnreadCountChanged?: (event: UnreadCountChangedEvent) => void
  onRecipientStateChanged?: (event: RecipientStateChangedEvent) => void
}

export interface UseInboxChannelReturn {
  isConnected: Ref<boolean>
}

/**
 * Subscribe to `communication:inbox#<userId>` — fixed-user channel
 * registered by Core\Communication\Base\CommunicationServiceProvider under
 * owner `communication`. Dispatches on wire events `communication.new` and
 * `communication.unread_count` declared by
 * Core\Communication\Base\Events\Broadcast\NewCommunicationReceived /
 * UnreadCountChanged.
 */
export function useInboxChannel(
  userId: MaybeRef<string | null | undefined>,
  options: UseInboxChannelOptions = {},
): UseInboxChannelReturn {
  const channel = computed(() => {
    const id = toValue(userId)
    return id ? `communication:inbox#${id}` : ''
  })

  const { isConnected } = useRealtimeSubscription<InboxPayload>(channel, {
    onMessage(frame: RealtimeFrame<InboxPayload>) {
      if (frame.event === 'communication.new') {
        options.onNewCommunication?.(frame.data as NewCommunicationEvent)
      } else if (frame.event === 'communication.unread_count') {
        options.onUnreadCountChanged?.(frame.data as UnreadCountChangedEvent)
      } else if (frame.event === 'communication.recipient_state') {
        options.onRecipientStateChanged?.(frame.data as RecipientStateChangedEvent)
      }
    },
  })

  return { isConnected }
}
